import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@mui/lab'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CallSplitIcon from '@mui/icons-material/CallSplit'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import RefreshIcon from '@mui/icons-material/Refresh'
import TimelineIcon from '@mui/icons-material/Timeline'
import ViewKanbanIcon from '@mui/icons-material/ViewKanban'

import PageHeader from '../../Shared/PageHeader'
import DataTable from '../../Shared/DataTable'
import LoadingBackdrop from '../../Shared/LoadingBackdrop'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import {
  cancelBreak,
  editProductionOrder,
  getBreakList,
  getProductionOrderId,
  setBreakCompleted,
  setBreakInProgress,
  splitOrderIntoBreaks,
} from '../../../services/productionOrderServices'
import {
  getRfidItemTimeline,
  getRfidItemsByOrder,
  getUniqueProductService,
} from '../../../services/productService'
import { mensagemDeErro } from '../../../services/apiClient'
import {
  acoesDaQuebra,
  getBreakStatus,
  getBreakStatusColor,
  getOrderStatus,
  getQualityStatus,
  getRfidSituation,
  isTerminalOrderStatus,
} from '../../../const/productionStatus'
import { formatDateTime, formatDuration, formatNumber } from '../../../utils/format'
import useUserNames from '../../../utils/useUserNames'
import { conversaoDoProduto, dividirQuantidade } from '../../../utils/quebras'
import PermissionComponent, { usePode } from '../../../permissions'
import { PERMISSOES } from '../../../const/permissoes'

const EMPTY_FORM = { numberOtherSystem: '', quantityTotal: '', observation: '' }
const EMPTY = '—'
const MAX_PREVIEW = 12

const percentual = quebra => {
  if (!quebra.quantityInitial) return 0
  return Math.min(100, Math.round((quebra.quantityFinished / quebra.quantityInitial) * 100))
}

/** Duração em minutos; `null` enquanto a etapa não tem término. */
const duracaoEmMinutos = (inicio, fim) => {
  if (!inicio || !fim) return null
  const diferenca = new Date(fim).getTime() - new Date(inicio).getTime()
  if (Number.isNaN(diferenca) || diferenca < 0) return null
  return diferenca / 60000
}

const SummaryItem = ({ label, children }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" component="div" sx={{ mt: 0.5 }}>
      {children}
    </Typography>
  </Grid>
)

/** Barra de progresso da ordem + contagem de quebras por estado. */
const ProgressoDaOrdem = ({ progress }) => {
  if (!progress) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Esta ordem ainda não foi fracionada — sem quebras não há progresso a acompanhar.
      </Alert>
    )
  }

  const contagens = [
    { chave: 'CONCLUIDA', rotulo: 'Concluídas', valor: progress.completed },
    { chave: 'EM_ANDAMENTO', rotulo: 'Em andamento', valor: progress.inProgress },
    { chave: 'ABERTA', rotulo: 'Abertas', valor: progress.open },
    { chave: 'CANCELADA', rotulo: 'Canceladas', valor: progress.cancelled },
  ]

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          {progress.totalBreaks} quebra(s)
        </Typography>
        <Typography variant="h6" fontWeight={600}>
          {Math.round(progress.percentComplete)}%
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, progress.percentComplete)}
        sx={{ height: 10, borderRadius: 5 }}
      />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
        {contagens.map(item => (
          <Chip
            key={item.chave}
            size="small"
            variant={item.valor ? 'filled' : 'outlined'}
            color={item.valor ? getBreakStatusColor(item.chave) : 'default'}
            label={`${item.rotulo}: ${item.valor}`}
          />
        ))}
      </Stack>
    </Box>
  )
}

/**
 * Histórico de RFID: por onde a peça passou, com serial, ciclo, lote, cavidade
 * e equipamento de cada etapa.
 *
 * Nas etapas migradas do sistema antigo o término nunca é nulo — lá a linha só
 * nascia quando a etapa acabava, então elas são histórico fechado. Etapa sem
 * término é produção acontecendo agora, e aparece destacada.
 */
const HistoricoDeEtapas = ({ stages }) => {
  // Quem iniciou e quem finalizou vêm como GUID; o nome é do SSO.
  const { nomeDe } = useUserNames(
    useMemo(
      () =>
        (stages ?? [])
          .flatMap(etapa => [etapa.startedByUserId, etapa.finishedByUserId])
          .filter(Boolean),
      [stages]
    )
  )

  if (!stages || stages.length === 0) {
    return (
      <Alert severity="info" variant="outlined">
        Nenhum apontamento de RFID registrado ainda.
      </Alert>
    )
  }

  return (
    <Timeline sx={{ m: 0, p: 0, '& .MuiTimelineOppositeContent-root': { flex: 0.25 } }}>
      {stages.map((etapa, indice) => {
        const emAberto = !etapa.finishedAt
        const qualidade = getQualityStatus(etapa.qualityStatus)
        const duracao = duracaoEmMinutos(etapa.startedAt, etapa.finishedAt)

        // Sempre visíveis, com "—" quando vazio: assim o layout fica igual em
        // toda etapa (como na versão original) e campos como Ciclo/Lote não
        // desaparecem — numa etapa de máquina o Ciclo mostra o valor, numa de
        // associação mostra "—", mas o campo está sempre lá.
        const detalhes = [
          ['Serial atual', etapa.serialCurrent || EMPTY],
          ['Serial anterior', etapa.serialPrevious || EMPTY],
          ['Ciclo', etapa.cycle || EMPTY],
          ['Lote', etapa.lot || EMPTY],
          ['Cavidade', etapa.cavity || EMPTY],
          ['Equipamento', etapa.equipmentId || EMPTY],
          ['Iniciou', etapa.startedByUserId ? nomeDe(etapa.startedByUserId) : EMPTY],
          ['Finalizou', etapa.finishedByUserId ? nomeDe(etapa.finishedByUserId) : EMPTY],
        ]

        return (
          <TimelineItem key={etapa.id}>
            <TimelineOppositeContent sx={{ pt: 1.2 }}>
              <Typography variant="caption" color="text.secondary" component="div">
                {formatDateTime(etapa.startedAt)}
              </Typography>
              {duracao !== null && (
                <Typography variant="caption" color="text.secondary" component="div">
                  {formatDuration(duracao)}
                </Typography>
              )}
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineDot
                color={emAberto ? 'warning' : 'success'}
                variant={emAberto ? 'outlined' : 'filled'}
              />
              {indice < stages.length - 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent sx={{ pb: 2.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle2">
                  {etapa.order}. {etapa.name || etapa.stageName || 'Etapa sem nome'}
                </Typography>
                <Chip
                  size="small"
                  label={etapa.qualityStatusLabel || qualidade.label}
                  color={qualidade.color}
                />
                {emAberto && <Chip size="small" color="warning" label="Em execução" />}
              </Stack>

              {detalhes.length > 0 && (
                <Box
                  sx={{
                    mt: 0.75,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                    gap: 0.75,
                  }}
                >
                  {detalhes.map(([rotulo, valor]) => (
                    <Box key={rotulo}>
                      <Typography variant="caption" color="text.secondary" component="div">
                        {rotulo}
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                        {valor}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {!emAberto && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{ mt: 0.5 }}
                >
                  Término: {formatDateTime(etapa.finishedAt)}
                </Typography>
              )}
            </TimelineContent>
          </TimelineItem>
        )
      })}
    </Timeline>
  )
}

const ProductionOrderDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { notify, FeedbackSnackbar } = useFeedback()

  const podeIniciar = usePode(PERMISSOES.QUEBRA_INICIAR)
  const podeConcluir = usePode(PERMISSOES.QUEBRA_CONCLUIR)
  const podeCancelarQuebra = usePode(PERMISSOES.QUEBRA_CANCELAR)
  const podeEditar = usePode(PERMISSOES.ORDEM_EDITAR)

  const [order, setOrder] = useState(null)
  // Ordens antigas guardam só o ID do responsável, sem `responsibleName`.
  const { nomeDe } = useUserNames(
    useMemo(() => (order?.responsibleUserId ? [order.responsibleUserId] : []), [order])
  )
  const [breaks, setBreaks] = useState([])
  const [rfidItems, setRfidItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingBreaks, setLoadingBreaks] = useState(false)
  const [loadingRfid, setLoadingRfid] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)

  const [aba, setAba] = useState(0)
  const [rfidPage, setRfidPage] = useState(0)
  const [rfidRowsPerPage, setRfidRowsPerPage] = useState(10)
  const [selecionadas, setSelecionadas] = useState([])
  const [quebraExpandida, setQuebraExpandida] = useState(null)

  const [dialogoDeQuebra, setDialogoDeQuebra] = useState(null)
  const [quantidadeConcluida, setQuantidadeConcluida] = useState('')
  const [observacaoDaQuebra, setObservacaoDaQuebra] = useState('')

  const [dialogoDeFracionamento, setDialogoDeFracionamento] = useState(false)
  const [quantidadeDeQuebras, setQuantidadeDeQuebras] = useState(1)

  const [epcSelecionado, setEpcSelecionado] = useState(null)
  const [etapasDoEpc, setEtapasDoEpc] = useState([])
  const [loadingEpc, setLoadingEpc] = useState(false)

  const [produto, setProduto] = useState(null)

  const changeForm = (field, value) => setForm(current => ({ ...current, [field]: value }))

  const fetchOrder = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getProductionOrderId(id)
      const data = response.data || null
      setOrder(data)
      setForm({
        numberOtherSystem: data?.numberOtherSystem || '',
        quantityTotal: data?.quantityTotal ?? '',
        observation: data?.observation || '',
      })
    } catch (error) {
      setOrder(null)
      notify(mensagemDeErro(error, 'Não foi possível carregar a ordem de produção.'), 'error')
    } finally {
      setLoading(false)
    }
  }, [id, notify])

  const fetchBreaks = useCallback(async () => {
    setLoadingBreaks(true)
    try {
      const response = await getBreakList(id)
      setBreaks(Array.isArray(response.data) ? response.data : [])
    } catch (error) {
      setBreaks([])
      notify(mensagemDeErro(error, 'Não foi possível carregar as quebras desta ordem.'), 'error')
    } finally {
      setLoadingBreaks(false)
    }
  }, [id, notify])

  const fetchRfidItems = useCallback(async () => {
    setLoadingRfid(true)
    try {
      const response = await getRfidItemsByOrder({ orderId: id })
      setRfidItems(response.data.result || [])
    } catch (error) {
      setRfidItems([])
      notify(mensagemDeErro(error, 'Não foi possível carregar as etiquetas desta ordem.'), 'error')
    } finally {
      setLoadingRfid(false)
    }
  }, [id, notify])

  useEffect(() => {
    fetchOrder()
    fetchBreaks()
    fetchRfidItems()
  }, [fetchOrder, fetchBreaks, fetchRfidItems])

  // Troca de ordem volta a aba RFID para a primeira página.
  useEffect(() => {
    setRfidPage(0)
  }, [id])

  /**
   * O fator de conversão fica no produto, não na ordem — que só guarda o nome
   * desnormalizado. Falha aqui não interrompe a tela: sem o fator o
   * fracionamento continua funcionando, só deixa de sugerir uma quebra por
   * embalagem.
   */
  useEffect(() => {
    if (!order?.productId) {
      setProduto(null)
      return
    }
    let ativo = true
    getUniqueProductService(order.productId)
      .then(response => ativo && setProduto(response.data))
      .catch(() => ativo && setProduto(null))
    return () => {
      ativo = false
    }
  }, [order?.productId])

  const recarregar = useCallback(() => {
    fetchOrder()
    fetchBreaks()
    fetchRfidItems()
    setSelecionadas([])
  }, [fetchOrder, fetchBreaks, fetchRfidItems])

  const status = getOrderStatus(order?.status)
  const locked = isTerminalOrderStatus(order?.status)

  /**
   * Saldo ainda não planejado — é ele que a API divide ao fracionar de novo,
   * e não a quantidade total. Quebra cancelada devolve a sua parte ao saldo.
   */
  const saldoNaoPlanejado = useMemo(() => {
    if (!order) return 0
    const planejado = breaks
      .filter(quebra => quebra.status !== 'CANCELADA')
      .reduce((soma, quebra) => soma + (quebra.quantityInitial || 0), 0)
    return Math.max(0, (order.quantityTotal || 0) - planejado)
  }, [order, breaks])

  const previaDoFracionamento = useMemo(
    () => dividirQuantidade(saldoNaoPlanejado, Number(quantidadeDeQuebras) || 0),
    [saldoNaoPlanejado, quantidadeDeQuebras]
  )

  /** Sugestão de "uma quebra por embalagem", calculada sobre o saldo. */
  const conversao = useMemo(
    () => conversaoDoProduto(saldoNaoPlanejado, produto),
    [saldoNaoPlanejado, produto]
  )

  const fracionamentoInvalido =
    Number(quantidadeDeQuebras) < 1 || Number(quantidadeDeQuebras) > saldoNaoPlanejado

  const iniciaveis = useMemo(
    () => breaks.filter(quebra => acoesDaQuebra(quebra.status).iniciar),
    [breaks]
  )

  const abrirFracionamento = () => {
    setQuantidadeDeQuebras(1)
    setDialogoDeFracionamento(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await editProductionOrder(id, {
        numberOtherSystem: form.numberOtherSystem || undefined,
        quantityTotal: form.quantityTotal === '' ? undefined : Number(form.quantityTotal),
        observation: form.observation || null,
      })
      notify('Ordem atualizada com sucesso!', 'success')
      fetchOrder()
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível salvar a ordem de produção.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const iniciarQuebra = async breakId => {
    setSaving(true)
    try {
      await setBreakInProgress(breakId)
      notify('Quebra iniciada.', 'success')
      recarregar()
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível iniciar a quebra.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  /**
   * Início em lote. Cada quebra é uma chamada independente: se uma falhar por
   * transição inválida, as outras seguem — e o resultado é reportado por
   * inteiro, em vez de a tela dizer "sucesso" tendo iniciado metade.
   */
  const iniciarSelecionadas = async () => {
    setSaving(true)
    const resultados = await Promise.allSettled(
      selecionadas.map(breakId => setBreakInProgress(breakId))
    )
    const falhas = resultados.filter(resultado => resultado.status === 'rejected')
    setSaving(false)

    if (falhas.length === 0) {
      notify(`${resultados.length} quebra(s) iniciada(s).`, 'success')
    } else if (falhas.length === resultados.length) {
      notify(mensagemDeErro(falhas[0].reason, 'Nenhuma quebra pôde ser iniciada.'), 'error')
    } else {
      notify(
        `${resultados.length - falhas.length} quebra(s) iniciada(s); ${falhas.length} falhou(ram).`,
        'warning'
      )
    }
    recarregar()
  }

  const abrirDialogo = (tipo, quebra) => {
    setDialogoDeQuebra({ tipo, quebra })
    setQuantidadeConcluida(String(quebra.quantityInitial ?? ''))
    setObservacaoDaQuebra('')
  }

  const confirmarDialogo = async () => {
    const { tipo, quebra } = dialogoDeQuebra
    setSaving(true)
    try {
      if (tipo === 'concluir') {
        await setBreakCompleted(quebra.id, {
          quantityFinished: quantidadeConcluida === '' ? undefined : Number(quantidadeConcluida),
          observation: observacaoDaQuebra || null,
        })
        notify('Quebra concluída.', 'success')
      } else {
        await cancelBreak(quebra.id, { observation: observacaoDaQuebra || null })
        notify('Quebra cancelada.', 'success')
      }
      setDialogoDeQuebra(null)
      recarregar()
    } catch (error) {
      notify(
        mensagemDeErro(
          error,
          tipo === 'concluir'
            ? 'Não foi possível concluir a quebra.'
            : 'Não foi possível cancelar a quebra.'
        ),
        'error'
      )
    } finally {
      setSaving(false)
    }
  }

  const fracionar = async () => {
    setSaving(true)
    try {
      const response = await splitOrderIntoBreaks(id, Number(quantidadeDeQuebras))
      notify(`${response.data.length} quebra(s) criada(s).`, 'success')
      setDialogoDeFracionamento(false)
      recarregar()
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível fracionar a ordem.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const abrirLinhaDoTempoDoEpc = async epc => {
    setEpcSelecionado(epc)
    setLoadingEpc(true)
    try {
      const response = await getRfidItemTimeline(epc)
      setEtapasDoEpc(response.data.result || [])
    } catch (error) {
      setEtapasDoEpc([])
      notify(
        mensagemDeErro(error, 'Não foi possível carregar a linha do tempo da etiqueta.'),
        'error'
      )
    } finally {
      setLoadingEpc(false)
    }
  }

  const alternarSelecao = breakId =>
    setSelecionadas(atual =>
      atual.includes(breakId) ? atual.filter(item => item !== breakId) : [...atual, breakId]
    )

  // Número da quebra a que a etiqueta pertence — usado na coluna e para agrupar.
  const numeroDaQuebra = useCallback(
    row => breaks.find(quebra => quebra.id === row.orderBreakId)?.numberBreak || '',
    [breaks]
  )

  // Ordena por quebra (depois por EPC): as etiquetas da mesma quebra ficam
  // juntas, agrupadas visualmente, em vez de espalhadas. Com paginação por cima,
  // já que uma ordem pode ter centenas de etiquetas.
  const rfidItemsOrdenados = useMemo(
    () =>
      [...rfidItems].sort((a, b) => {
        const qa = numeroDaQuebra(a)
        const qb = numeroDaQuebra(b)
        if (qa !== qb) return String(qa).localeCompare(String(qb), undefined, { numeric: true })
        return String(a.epc || '').localeCompare(String(b.epc || ''))
      }),
    [rfidItems, numeroDaQuebra]
  )

  const rfidPaginados = useMemo(
    () =>
      rfidItemsOrdenados.slice(
        rfidPage * rfidRowsPerPage,
        rfidPage * rfidRowsPerPage + rfidRowsPerPage
      ),
    [rfidItemsOrdenados, rfidPage, rfidRowsPerPage]
  )

  const rfidColumns = [
    {
      id: 'epc',
      label: 'EPC',
      element: row => (
        <Tooltip title="Ver por onde esta etiqueta passou">
          <Button
            size="small"
            startIcon={<TimelineIcon fontSize="small" />}
            onClick={() => abrirLinhaDoTempoDoEpc(row.epc)}
            sx={{ textTransform: 'none', fontFamily: 'monospace' }}
          >
            {row.epc}
          </Button>
        </Tooltip>
      ),
    },
    { id: 'product', label: 'Produto', element: row => row.productName || EMPTY },
    {
      id: 'situation',
      label: 'Situação',
      element: row => {
        const situacao = getRfidSituation(row.situation)
        return (
          <Chip size="small" label={row.situationLabel || situacao.label} color={situacao.color} />
        )
      },
    },
    {
      id: 'break',
      label: 'Quebra',
      element: row => numeroDaQuebra(row) || EMPTY,
    },
    { id: 'lot', label: 'Lote', element: row => row.lot || EMPTY },
    {
      id: 'prints',
      label: 'Impressões',
      align: 'right',
      element: row => formatNumber(row.printCount),
    },
    { id: 'lastRead', label: 'Última leitura', element: row => formatDateTime(row.lastReadAt) },
  ]

  return (
    <Box>
      <PageHeader
        title="Detalhe da Ordem de Produção"
        subtitle={order?.numberBihands || id}
        actions={
          <>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={recarregar}>
              Atualizar
            </Button>
            <PermissionComponent permissao={PERMISSOES.KANBAN_VISUALIZAR}>
              <Button
                variant="outlined"
                startIcon={<ViewKanbanIcon />}
                onClick={() => navigate('/MovBreakKanban')}
              >
                Kanban
              </Button>
            </PermissionComponent>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/MovBreak')}
            >
              Voltar
            </Button>
          </>
        }
      />

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Resumo
            </Typography>
            <Chip label={order?.statusLabel || status.label} color={status.color} />
          </Stack>

          <Grid container spacing={2}>
            <SummaryItem label="Ordem Bihands">{order?.numberBihands || EMPTY}</SummaryItem>
            <SummaryItem label="Ordem (outro sistema)">
              {order?.numberOtherSystem || EMPTY}
            </SummaryItem>
            <SummaryItem label="Produto">{order?.productName || EMPTY}</SummaryItem>
            <SummaryItem label="Cliente">
              {order?.clientName
                ? [order.clientCode, order.clientName].filter(Boolean).join(' - ')
                : EMPTY}
            </SummaryItem>
            <SummaryItem label="Responsável">
              {order?.responsibleName ||
                (order?.responsibleUserId ? nomeDe(order.responsibleUserId) : EMPTY)}
            </SummaryItem>
            <SummaryItem label="Quantidade total">{formatNumber(order?.quantityTotal)}</SummaryItem>
            <SummaryItem label="Quantidade concluída">
              {formatNumber(order?.quantityFinished)}
            </SummaryItem>
            <SummaryItem label="Criada em">{formatDateTime(order?.createdAt)}</SummaryItem>
            <SummaryItem label="Etiquetas vinculadas">{formatNumber(rfidItems.length)}</SummaryItem>
          </Grid>

          {/* Só aparece quando a ordem tem mais de um produto: 99,35% delas têm
              exatamente um, e listar um item só repetiria o campo "Produto". */}
          {order?.items?.length > 1 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                Produtos da ordem
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {order.items.map(item => (
                  <Chip
                    key={item.id}
                    size="small"
                    variant={item.finishGood ? 'filled' : 'outlined'}
                    color={item.finishGood ? 'primary' : 'default'}
                    label={`${item.productName || 'Produto sem nome'} · ${formatNumber(
                      item.quantity
                    )}${item.finishGood ? ' · final' : ''}`}
                  />
                ))}
              </Stack>
            </>
          )}

          <ProgressoDaOrdem progress={order?.progress} />
        </CardContent>
      </Card>

      <Tabs value={aba} onChange={(event, valor) => setAba(valor)} sx={{ mb: 2 }}>
        <Tab label={`Quebras (${breaks.length})`} />
        <Tab label={`Etiquetas RFID (${rfidItems.length})`} />
        <Tab label="Edição da ordem" />
      </Tabs>

      {aba === 0 && (
        <>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ sm: 'center' }}
            sx={{ mb: 2 }}
          >
            <PermissionComponent permissao={PERMISSOES.QUEBRA_INICIAR}>
              <Button
                variant="contained"
                startIcon={<PlayCircleOutlineIcon />}
                disabled={selecionadas.length === 0 || saving}
                onClick={iniciarSelecionadas}
              >
                Iniciar selecionadas ({selecionadas.length})
              </Button>
            </PermissionComponent>
            <Button
              color="inherit"
              disabled={iniciaveis.length === 0}
              onClick={() =>
                setSelecionadas(
                  selecionadas.length === iniciaveis.length ? [] : iniciaveis.map(item => item.id)
                )
              }
            >
              {selecionadas.length === iniciaveis.length && iniciaveis.length > 0
                ? 'Limpar seleção'
                : `Selecionar abertas (${iniciaveis.length})`}
            </Button>
            <Box sx={{ flex: 1 }} />
            <PermissionComponent permissao={PERMISSOES.ORDEM_EDITAR}>
              <Tooltip
                title={
                  saldoNaoPlanejado > 0
                    ? `${formatNumber(saldoNaoPlanejado)} peça(s) ainda sem quebra`
                    : 'Toda a quantidade da ordem já está planejada em quebras'
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<CallSplitIcon />}
                    disabled={locked || saldoNaoPlanejado <= 0}
                    onClick={abrirFracionamento}
                  >
                    Fracionar
                  </Button>
                </span>
              </Tooltip>
            </PermissionComponent>
          </Stack>

          {!loadingBreaks && breaks.length === 0 && (
            <Alert
              severity="info"
              action={
                podeEditar && !locked ? (
                  <Button size="small" startIcon={<CallSplitIcon />} onClick={abrirFracionamento}>
                    Fracionar agora
                  </Button>
                ) : null
              }
            >
              Esta ordem ainda não está fracionada. É a quebra que o chão de fábrica aponta — sem
              ela não há como acompanhar a produção nem associar etiquetas.
            </Alert>
          )}

          {loadingBreaks && <LinearProgress sx={{ mb: 2 }} />}

          {breaks.map(quebra => {
            const estado = getBreakStatus(quebra.status)
            const acoes = acoesDaQuebra(quebra.status)
            const progresso = percentual(quebra)

            return (
              <Accordion
                key={quebra.id}
                expanded={quebraExpandida === quebra.id}
                onChange={(event, aberto) => setQuebraExpandida(aberto ? quebra.id : null)}
                sx={{ borderLeft: `4px solid ${estado.color}` }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs="auto">
                      <Checkbox
                        size="small"
                        disabled={!acoes.iniciar || !podeIniciar}
                        checked={selecionadas.includes(quebra.id)}
                        onClick={event => event.stopPropagation()}
                        onChange={() => alternarSelecao(quebra.id)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="subtitle2">{quebra.numberBreak || EMPTY}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {quebra.currentStage?.name || 'Sem etapa atual'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <LinearProgress
                        variant="determinate"
                        value={progresso}
                        color={getBreakStatusColor(quebra.status)}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatNumber(quebra.quantityFinished)} de{' '}
                        {formatNumber(quebra.quantityInitial)} ({progresso}%)
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Chip
                        size="small"
                        label={quebra.statusLabel || estado.label}
                        sx={{ backgroundColor: estado.color, color: '#fff' }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="caption" color="text.secondary" component="div">
                        Início: {formatDateTime(quebra.startedAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" component="div">
                        Término: {formatDateTime(quebra.finishedAt)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm="auto" sx={{ ml: { sm: 'auto' } }}>
                      <Stack direction="row" onClick={event => event.stopPropagation()}>
                        {acoes.iniciar && podeIniciar && (
                          <Tooltip title="Iniciar quebra">
                            <IconButton color="primary" onClick={() => iniciarQuebra(quebra.id)}>
                              <PlayCircleOutlineIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {acoes.concluir && podeConcluir && (
                          <Tooltip title="Concluir quebra">
                            <IconButton
                              color="success"
                              onClick={() => abrirDialogo('concluir', quebra)}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {acoes.cancelar && podeCancelarQuebra && (
                          <Tooltip title="Cancelar quebra">
                            <IconButton
                              color="error"
                              onClick={() => abrirDialogo('cancelar', quebra)}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: 'action.hover' }}>
                  {quebra.cancelReason && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      Motivo do cancelamento: {quebra.cancelReason}
                    </Alert>
                  )}
                  {quebra.observation && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {quebra.observation}
                    </Alert>
                  )}
                  <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                    Histórico de RFID e etapas
                  </Typography>
                  <HistoricoDeEtapas stages={quebra.stages} />
                </AccordionDetails>
              </Accordion>
            )
          })}
        </>
      )}

      {aba === 1 && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Etiquetas atreladas a esta ordem pelo apontamento de chão de fábrica. Clique no EPC
            para ver por onde a peça passou.
          </Alert>
          <DataTable
            columns={rfidColumns}
            rows={rfidPaginados}
            loading={loadingRfid}
            emptyMessage="Nenhuma etiqueta RFID vinculada a esta ordem."
            page={rfidPage}
            rowsPerPage={rfidRowsPerPage}
            total={rfidItems.length}
            onPageChange={(event, novaPagina) => setRfidPage(novaPagina)}
            onRowsPerPageChange={event => {
              setRfidRowsPerPage(parseInt(event.target.value, 10))
              setRfidPage(0)
            }}
            rowKey={row => row.id}
          />
        </>
      )}

      {aba === 2 && (
        <Card variant="outlined">
          <CardContent>
            {locked && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {`Esta ordem está com o status "${status.label}" e por isso não pode mais ser editada.`}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Ordem (outro sistema)"
                  disabled={locked}
                  value={form.numberOtherSystem}
                  onChange={event => changeForm('numberOtherSystem', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Quantidade"
                  disabled={locked}
                  value={form.quantityTotal}
                  onChange={event => changeForm('quantityTotal', event.target.value)}
                  helperText="Aumentar a quantidade libera saldo para novas quebras."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  size="small"
                  label="Observações"
                  disabled={locked}
                  value={form.observation}
                  onChange={event => changeForm('observation', event.target.value)}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <PermissionComponent permissao={PERMISSOES.ORDEM_EDITAR}>
                <Button variant="contained" disabled={locked || saving} onClick={handleSave}>
                  Salvar
                </Button>
              </PermissionComponent>
            </Box>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={Boolean(dialogoDeQuebra)}
        onClose={() => setDialogoDeQuebra(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {dialogoDeQuebra?.tipo === 'concluir' ? 'Concluir quebra' : 'Cancelar quebra'}{' '}
          {dialogoDeQuebra?.quebra.numberBreak}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {dialogoDeQuebra?.tipo === 'concluir' && (
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Quantidade produzida"
                inputProps={{ min: 0 }}
                value={quantidadeConcluida}
                onChange={event => setQuantidadeConcluida(event.target.value)}
                helperText={`Planejado: ${formatNumber(dialogoDeQuebra?.quebra.quantityInitial)}`}
              />
            )}
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              label={
                dialogoDeQuebra?.tipo === 'concluir' ? 'Observação (opcional)' : 'Motivo (opcional)'
              }
              value={observacaoDaQuebra}
              onChange={event => setObservacaoDaQuebra(event.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setDialogoDeQuebra(null)}>
            Voltar
          </Button>
          <Button
            variant="contained"
            color={dialogoDeQuebra?.tipo === 'concluir' ? 'success' : 'error'}
            disabled={saving}
            onClick={confirmarDialogo}
          >
            {dialogoDeQuebra?.tipo === 'concluir' ? 'Concluir' : 'Cancelar quebra'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogoDeFracionamento}
        onClose={() => setDialogoDeFracionamento(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Fracionar a produção</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            O saldo ainda não planejado é de <strong>{formatNumber(saldoNaoPlanejado)}</strong>{' '}
            peça(s). É ele que será dividido — as quebras existentes não são alteradas.
          </Typography>
          {conversao?.packages > 0 && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button
                  size="small"
                  disabled={Number(quantidadeDeQuebras) === conversao.packages}
                  onClick={() => setQuantidadeDeQuebras(conversao.packages)}
                >
                  Usar {conversao.packages}
                </Button>
              }
            >
              {`Contado em ${conversao.unitLabel.toLowerCase()} de `}
              <strong>{formatNumber(conversao.piece)}</strong>
              {` peças — o saldo dá `}
              <strong>{conversao.packages}</strong>
              {` ${conversao.packages === 1 ? 'embalagem' : 'embalagens'}`}
              {saldoNaoPlanejado % conversao.piece !== 0 && ' (a última incompleta)'}.
            </Alert>
          )}
          <TextField
            fullWidth
            autoFocus
            size="small"
            type="number"
            label="Quantidade de quebras"
            inputProps={{ min: 1, max: 1000 }}
            value={quantidadeDeQuebras}
            onChange={event => setQuantidadeDeQuebras(event.target.value)}
          />
          {!fracionamentoInvalido && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Prévia da divisão
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {previaDoFracionamento.slice(0, MAX_PREVIEW).map((quantidade, indice) => (
                  <Chip
                    key={indice}
                    size="small"
                    variant="outlined"
                    label={formatNumber(quantidade)}
                  />
                ))}
                {previaDoFracionamento.length > MAX_PREVIEW && (
                  <Chip size="small" label={`+${previaDoFracionamento.length - MAX_PREVIEW}`} />
                )}
              </Box>
            </Box>
          )}
          {Number(quantidadeDeQuebras) > saldoNaoPlanejado && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Mais quebras do que peças no saldo: alguma ficaria com quantidade zero.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setDialogoDeFracionamento(false)}>
            Voltar
          </Button>
          <Button variant="contained" disabled={saving || fracionamentoInvalido} onClick={fracionar}>
            Fracionar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(epcSelecionado)}
        onClose={() => setEpcSelecionado(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'monospace' }}>{epcSelecionado}</DialogTitle>
        <DialogContent>
          {loadingEpc && <LinearProgress />}
          {!loadingEpc && <HistoricoDeEtapas stages={etapasDoEpc} />}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => navigate(`/RfidTimeline/${encodeURIComponent(epcSelecionado)}`)}>
            Abrir tela completa
          </Button>
          <Button variant="contained" onClick={() => setEpcSelecionado(null)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <LoadingBackdrop open={loading || saving} message="Processando..." />
      <FeedbackSnackbar />
    </Box>
  )
}

export default ProductionOrderDetail
